import { Accordion, Input, Menu, ScrollArea } from "@mantine/core"
import React, { useMemo, useState } from "react"
import { Icon, Button } from "@/components"
import { useStyles } from "@/pages/texting/components/PlaceholdersMenuDropdown/PlaceholdersMenuDropdown.styles"
import type { PlaceholderButtonMenuProps } from "@/pages/texting/types"
import { useFormContext } from "react-hook-form"
import { AccordionSection } from "@/pages/texting/components/PlaceholdersMenuDropdown/AccordionSection"
import { BulkSMSIconsPlaceholdersGroup, mapIconToPlaceholderGroup } from "@/services/texting"

export const PlaceholderButtonMenu = ({
    placeholders,
    cursorTextRef,
    iconsList,
    dataCy,
    inputName = "text",
}: PlaceholderButtonMenuProps) => {
    const [opened, setOpened] = useState(false)
    const [searchValue, setSearchValue] = useState("")
    const { classes } = useStyles()
    const { watch, setValue } = useFormContext()

    const textareaValue = watch(inputName)

    const toggle = () => {
        setOpened(!opened)
    }

    const filteredPlaceholdersList = useMemo(() => {
        const value = searchValue.toLowerCase()
        if (!value) return placeholders

        const filtered = placeholders
            .map((group) => {
                const items = group.items.filter((item) => item.label.toLowerCase().includes(value))
                return { ...group, items }
            })
            .filter((group) => group.items.length > 0)

        return filtered
    }, [placeholders, searchValue])

    const insertPlaceholder = (placeholder: string) => {
        if (!textareaValue) {
            setValue(inputName, placeholder)
            return
        }

        const startPos = cursorTextRef.current.start
        const endPos = cursorTextRef.current.end
        const newValue =
            textareaValue.substring(0, startPos) + placeholder + textareaValue.substring(endPos, textareaValue.length)
        setValue(inputName, newValue)
    }

    const onSelection = (placeholder: string) => {
        toggle()
        insertPlaceholder(placeholder)
        setSearchValue("")
    }

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchValue(event.currentTarget.value)
    }

    return (
        <Menu onClose={toggle} position="bottom-start" radius={16} offset={2} opened={opened} shadow="xl" width={380}>
            <Menu.Target>
                <Button
                    data-cy={dataCy}
                    size="xs"
                    className={classes.buttonTarget}
                    variant="outline"
                    color="gray"
                    onClick={toggle}
                >
                    <Icon className={classes.iconTarget} icon="brackets-curly" iconFamily="far" />
                </Button>
            </Menu.Target>

            <Menu.Dropdown className={classes.menuDropdown}>
                <Input
                    className={classes.searchInput}
                    icon={<Icon icon="search" iconFamily="far" color="gray" />}
                    radius="lg"
                    onChange={handleSearchChange}
                    value={searchValue}
                    size="xs"
                    name="search-placeholders"
                    placeholder="Search placeholders"
                />
                <ScrollArea h={400} type="scroll">
                    {filteredPlaceholdersList.map((item, key) => (
                        <Menu.Item className={classes.menuItem} key={key} closeMenuOnClick={false}>
                            <Accordion
                                defaultValue={Object.keys(BulkSMSIconsPlaceholdersGroup)[0]}
                                radius={12}
                                className={classes.accordionWrapper}
                            >
                                <AccordionSection
                                    icon={mapIconToPlaceholderGroup(item.group, iconsList)}
                                    sectionName={item.group}
                                    items={item.items}
                                    onSelection={onSelection}
                                />
                            </Accordion>
                        </Menu.Item>
                    ))}
                </ScrollArea>
            </Menu.Dropdown>
        </Menu>
    )
}
